/**
 * My Bag — the Gap Map (MY_BAG_PLAN.md §4).
 *
 * The yardage ladder: vertical on mobile (driver top, LW bottom), horizontal on
 * desktop. One rung per club at its carry, one connector per gap drawn *to
 * scale* so an overlap makes two rungs visibly collide and a hole stretches the
 * connector into an obvious void. Benched clubs draw as ghosted rungs and go
 * back in the bag with one tap (§4).
 *
 * Both orientations read the same `GapMapModel`, so they cannot disagree about
 * what a gap is. Nothing here decides a colour: tone comes from whether the
 * server put that pair in `holes` or `overlaps` (§3.5's tolerance is
 * provenance-scaled and lives on the backend).
 *
 * The scale has a truncated baseline. That is deliberate and it is honest: a
 * linear scale preserves differences exactly wherever its origin sits, gaps are
 * the only thing this chart is about, and 13 rungs from a 0y origin on a phone
 * would compress every gap into noise. The axis prints its own yardages.
 *
 * §9.6: no new palette, no new iconography. Existing tokens only.
 */
import React from 'react';

import ProvenanceChip from './ProvenanceChip';
import {
  GapMapModel,
  GapTone,
  MapGap,
  MapRung,
  axisTicks,
  fractionOf,
  roundYards,
} from './gapMapModel';

export interface GapMapProps {
  model: GapMapModel;
  /** `true` while the Apply-recommendations toggle is on. */
  applied: boolean;
  /** Puts a benched rung back in the bag — §4's "one tap". */
  onUnbench?: (slotOrder: number) => void;
}

const TONE_BAR: Record<GapTone, string> = {
  healthy: 'bg-score-3',
  overlap: 'bg-score-2',
  hole: 'bg-score-0',
};

const TONE_CHIP: Record<GapTone, string> = {
  healthy: 'bg-score-3-bg text-score-3',
  overlap: 'bg-score-2-bg text-score-2',
  hole: 'bg-score-0-bg text-score-0',
};

const TONE_WORD: Record<GapTone, string> = {
  healthy: 'in range',
  overlap: 'overlap — one slot is doing nothing',
  hole: 'hole — bigger than your target gap plus its tolerance',
};

const gapTitle = (g: MapGap): string => {
  const tol = g.tolYards === null ? '' : ` · tolerance ±${roundYards(g.tolYards)}y`;
  return `${g.lowerLabel} to ${g.upperLabel}: ${roundYards(g.yards)}y — ${TONE_WORD[g.tone]}${tol}`;
};

const rungBar = (r: MapRung): string => {
  if (r.benched) return 'border border-dashed border-line-2 bg-transparent';
  if (r.bendDelta !== 0) return 'bg-green-ink';
  return 'bg-slate';
};

const deltaText = (delta: number): string =>
  `${delta > 0 ? '+' : '−'}${Math.abs(roundYards(delta))}y`;

// ---------------------------------------------------------------------------
// Mobile — the ladder runs top to bottom, long club first.
// ---------------------------------------------------------------------------

const LABEL_COL = 66;
const VALUE_COL = 62;

const VerticalLadder: React.FC<GapMapProps> = ({ model, applied, onUnbench }) => {
  const ticks = axisTicks(model);
  // Ghosts are interleaved by carry so the ladder reads as one ladder, but they
  // never carry a gap connector — a benched club is off the ladder (§4).
  const drawn = model.rungs
    .concat(model.ghosts)
    .slice()
    .sort((a, b) => b.carry - a.carry);
  /* Each gap hangs under its longer club, so one lookup by that club's label is
     enough — a rung is the upper end of at most one gap. */
  const gapUnder = new Map<string, { gap: MapGap; lowFrac: number; highFrac: number }>();
  model.gaps.forEach((g, i) => {
    gapUnder.set(g.upperLabel, {
      gap: g,
      highFrac: fractionOf(model, model.rungs[i].carry),
      lowFrac: fractionOf(model, model.rungs[i + 1].carry),
    });
  });

  return (
    <div className="relative">
      {/* Gridlines, behind everything, spanning only the track column. */}
      <div
        className="pointer-events-none absolute inset-y-0"
        style={{ left: LABEL_COL, right: VALUE_COL }}
        aria-hidden="true"
      >
        {ticks.map((t) => (
          <div
            key={t}
            className="absolute inset-y-0 w-px bg-line"
            style={{ left: `${fractionOf(model, t) * 100}%` }}
          />
        ))}
      </div>

      <ol className="relative">
        {drawn.map((r) => {
          const below = r.benched ? undefined : gapUnder.get(r.label);
          const frac = fractionOf(model, r.carry);
          const body = (
            <div className="flex items-center" style={{ minHeight: 34 }}>
              <div
                className="flex shrink-0 items-center gap-1.5 font-mono text-sm text-ink"
                style={{ width: LABEL_COL }}
              >
                {r.label}
                {/* The dot alone: thirteen labelled chips is a second line per
                    rung, which doubles the scroll on a phone for no new fact. */}
                <ProvenanceChip
                  provenance={r.provenance}
                  confidence={r.confidence}
                  showLabel={false}
                />
              </div>
              <div className="relative flex-1" style={{ height: 22 }}>
                <div
                  className={`absolute inset-y-0 left-0 rounded-brand-sm transition-[width] duration-500 ${rungBar(
                    r
                  )} ${r.swapProposed ? 'ring-1 ring-cyan-ink' : ''}`}
                  style={{ width: `${Math.max(frac * 100, 1.5)}%` }}
                />
              </div>
              <div
                className="shrink-0 text-right font-mono text-sm tabular-nums text-ink"
                style={{ width: VALUE_COL }}
              >
                {roundYards(r.carry)}y
              </div>
            </div>
          );

          return (
            <li key={r.label}>
              {r.benched && onUnbench ? (
                <button
                  type="button"
                  onClick={() => onUnbench(r.slotOrder)}
                  title={`${r.label} is benched — put it back in the bag`}
                  className="w-full text-left opacity-60 hover:opacity-100"
                >
                  {body}
                </button>
              ) : (
                body
              )}

              {(r.benched || (applied && (r.bendDelta !== 0 || r.swapProposed))) && (
                <div
                  className="flex flex-wrap items-center gap-1.5 pb-1"
                  style={{ paddingLeft: LABEL_COL }}
                >
                  {r.benched && (
                    <span className="rounded-brand-sm border border-dashed border-line-2 px-1.5 py-0.5 text-xs text-ink-3">
                      benched{onUnbench ? ' · tap to put back' : ''}
                    </span>
                  )}
                  {applied && r.bendDelta !== 0 && (
                    <span className="rounded-brand-sm bg-green/15 px-1.5 py-0.5 text-xs text-green-ink">
                      bent {deltaText(r.bendDelta)} · was {roundYards(r.carryBefore)}y
                    </span>
                  )}
                  {applied && r.swapProposed && (
                    <span className="rounded-brand-sm border border-cyan/40 px-1.5 py-0.5 text-xs text-cyan-ink">
                      swap proposed — the rung cannot move, we don&apos;t know the new
                      club&apos;s carry
                    </span>
                  )}
                </div>
              )}

              {below && (
                <div
                  className="flex items-center"
                  style={{ height: 26 }}
                  title={gapTitle(below.gap)}
                >
                  <div className="shrink-0" style={{ width: LABEL_COL }} />
                  <div className="relative h-full flex-1">
                    <div
                      className={`absolute top-1/2 h-1 -translate-y-1/2 rounded-full transition-all duration-500 ${
                        TONE_BAR[below.gap.tone]
                      }`}
                      style={{
                        left: `${below.lowFrac * 100}%`,
                        width: `${Math.max((below.highFrac - below.lowFrac) * 100, 0.4)}%`,
                      }}
                    />
                  </div>
                  <div className="shrink-0 text-right" style={{ width: VALUE_COL }}>
                    <span
                      className={`inline-block rounded-brand-sm px-1.5 py-0.5 font-mono text-xs tabular-nums ${
                        TONE_CHIP[below.gap.tone]
                      }`}
                    >
                      {roundYards(below.gap.yards)}y
                    </span>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* The axis, so the truncated baseline is stated rather than implied. */}
      <div className="relative mt-1" style={{ marginLeft: LABEL_COL, marginRight: VALUE_COL }}>
        <div className="h-px w-full bg-line-2" />
        <div className="relative h-4">
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute -translate-x-1/2 font-mono text-xs tabular-nums text-ink-3"
              style={{ left: `${fractionOf(model, t) * 100}%` }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Desktop — the ladder runs left to right, long club first.
// ---------------------------------------------------------------------------

const BAR_AREA = 260;

const HorizontalLadder: React.FC<GapMapProps> = ({ model, applied, onUnbench }) => {
  const ticks = axisTicks(model);
  const drawn = model.rungs
    .concat(model.ghosts)
    .slice()
    .sort((a, b) => b.carry - a.carry);
  const n = drawn.length;
  const columnOf = new Map<string, number>();
  drawn.forEach((r, i) => columnOf.set(r.label, i));

  /* A connector hangs off the boundary immediately right of its longer club.
     Boundaries are one column apart, so the yardage chips can never collide
     however small the gap is — only the segment between them shrinks, which is
     the part carrying the information. */
  const connectors = model.gaps.map((g, i) => ({
    g,
    x: (((columnOf.get(g.upperLabel) ?? i) + 1) / n) * 100,
    topFrac: fractionOf(model, model.rungs[i].carry),
    bottomFrac: fractionOf(model, model.rungs[i + 1].carry),
  }));

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: Math.max(n * 46, 320) }}>
        <div className="flex">
          <div className="relative shrink-0 pr-2" style={{ width: 44, height: BAR_AREA }}>
            {ticks.map((t) => (
              <span
                key={t}
                className="absolute right-2 -translate-y-1/2 font-mono text-xs tabular-nums text-ink-3"
                style={{ top: `${(1 - fractionOf(model, t)) * 100}%` }}
              >
                {t}
              </span>
            ))}
          </div>

          <div className="relative flex-1" style={{ height: BAR_AREA }}>
            {ticks.map((t) => (
              <div
                key={t}
                className="pointer-events-none absolute inset-x-0 h-px bg-line"
                style={{ top: `${(1 - fractionOf(model, t)) * 100}%` }}
                aria-hidden="true"
              />
            ))}

            <div
              className="absolute inset-0 grid items-end"
              style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
            >
              {drawn.map((r) => (
                <div key={r.label} className="flex h-full items-end justify-center px-1">
                  <div
                    className={`w-full max-w-[34px] rounded-t-brand-sm transition-[height] duration-500 ${rungBar(
                      r
                    )} ${r.swapProposed ? 'ring-1 ring-cyan-ink' : ''}`}
                    style={{ height: `${Math.max(fractionOf(model, r.carry) * 100, 1.5)}%` }}
                    title={`${r.label} · ${roundYards(r.carry)}y${
                      r.benched ? ' · benched' : ''
                    }`}
                  />
                </div>
              ))}
            </div>

            {connectors.map(({ g, x, topFrac, bottomFrac }) => (
              <div
                key={`${g.upperLabel}-${g.lowerLabel}`}
                className="pointer-events-auto absolute"
                style={{
                  left: `${x}%`,
                  top: `${(1 - topFrac) * 100}%`,
                  height: `${Math.max((topFrac - bottomFrac) * 100, 0.4)}%`,
                }}
                title={gapTitle(g)}
              >
                <div
                  className={`absolute inset-y-0 left-0 w-1 -translate-x-1/2 rounded-full transition-all duration-500 ${
                    TONE_BAR[g.tone]
                  }`}
                />
                <span
                  className={`absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-brand-sm px-1 py-0.5 font-mono text-xs tabular-nums ${
                    TONE_CHIP[g.tone]
                  }`}
                >
                  {roundYards(g.yards)}y
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex">
          <div className="shrink-0" style={{ width: 44 }} />
          <div
            className="grid flex-1 border-t border-line-2 pt-2"
            style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
          >
            {drawn.map((r) => (
              <div key={r.label} className="px-0.5 text-center">
                <div className="font-mono text-sm text-ink">{r.label}</div>
                <div className="font-mono text-xs tabular-nums text-ink-2">
                  {roundYards(r.carry)}y
                </div>
                {applied && r.bendDelta !== 0 && (
                  <div className="font-mono text-xs text-green-ink">{deltaText(r.bendDelta)}</div>
                )}
                {r.benched &&
                  (onUnbench ? (
                    <button
                      type="button"
                      onClick={() => onUnbench(r.slotOrder)}
                      className="mt-1 rounded-brand-sm border border-dashed border-line-2 px-1 text-xs text-ink-3 hover:text-ink"
                      title={`Put the ${r.label} back in the bag`}
                    >
                      benched
                    </button>
                  ) : (
                    <div className="text-xs text-ink-3">benched</div>
                  ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const Legend: React.FC = () => (
  <div className="flex flex-wrap items-center gap-3 text-xs text-ink-2">
    {(['healthy', 'overlap', 'hole'] as GapTone[]).map((t) => (
      <span key={t} className="inline-flex items-center gap-1.5">
        <span className={`inline-block h-2 w-5 rounded-full ${TONE_BAR[t]}`} />
        {t === 'healthy' ? 'in range' : t}
      </span>
    ))}
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2 w-5 rounded-full border border-dashed border-line-2" />
      benched
    </span>
  </div>
);

const GapMap: React.FC<GapMapProps> = (props) => {
  const { model } = props;

  if (model.rungs.length === 0) {
    return (
      <div className="rounded-brand border border-dashed border-line-2 bg-paper p-6 text-center">
        <p className="text-base text-ink">Nothing on the ladder carries a distance yet.</p>
        <p className="mt-1 text-sm text-ink-2">
          Fill in a couple of carries on the Rack and the map draws itself.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-brand border border-line bg-paper p-4 shadow-raised">
      <div className="md:hidden">
        <VerticalLadder {...props} />
      </div>
      <div className="hidden md:block">
        <HorizontalLadder {...props} />
      </div>

      <div className="mt-4 space-y-2 border-t border-line pt-3">
        <Legend />
        <p className="text-xs text-ink-3">
          The yardage axis starts at {model.domainLow}y, not at zero — gaps are what this chart
          is about and a zero origin would squash them. Differences are drawn to scale either way.
        </p>
        {model.putterLabels.length > 0 && (
          <p className="text-xs text-ink-3">
            {model.putterLabels.join(', ')} is one of the fourteen and holds no distance, so it
            never gaps.
          </p>
        )}
        {model.missingLabels.length > 0 && (
          <p className="text-xs text-ink-3">
            No carry yet for {model.missingLabels.join(', ')} — the map draws around them.
          </p>
        )}
      </div>
    </div>
  );
};

export default GapMap;
